import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/es';
import * as citasApiService from '@/services/citasApiService';
import toastService from '@/utils/toastService';
import Modal from '@/components/common/Modal';
import { useCalendario } from '@/hooks/useCalendario';
import citasLogicService from '@/services/citasLogicService';
import horarioService from '@/services/horarioService';
import { normalizeDataObject } from '@/utils/normalizationUtils';

// Importar componentes
import {
  CalendarHeader,
  CalendarGrid,
  TimeSelector,
  AppointmentList,
  AppointmentModal
} from './components';

// Configurar moment para usar español con lunes como primer día
moment.locale('es', {
  week: {
    dow: 1, // Lunes como primer día de la semana
  },
});

/**
 * Componente de calendario moderno para la selección de citas
 */
const CalendarioCitas = () => {
  // Usar el hook personalizado para toda la lógica del calendario
  const {
    currentDate,
    selectedDate,
    selectedTime,
    showTimeSelector,
    clientes,
    vehiculos,
    servicios,
    selectedServicios,
    tiempoTotal,
    horaFin,
    horaFinAjustada,
    loading,
    setLoading,
    isMobile,
    selectedCliente,
    setSelectedCliente,
    generateTimeSlots,
    isTimeSlotAvailable,
    hasCitasOnDay,
    getCitasOnDay,
    getCapacidadInfo,
    handleSelectDate,
    handleSelectTime,
    handleServicioSelect,
    handleMonthChange,
    handleGoToToday,
    handleCloseTimeSelector,
    handleDeseleccionarTodos,
    handleForceShowTimeSelector,
    refreshCitas,
    fetchServicios,
    selectedSucursal,
    sucursalData,
    isAdmin,
    isSuperAdmin,
    clienteData
  } = useCalendario();

  // Estados específicos del modal y formulario
  const [formData, setFormData] = useState({
    Vehiculo: '',
    Motivo: '',
    Estado: 'Pendiente',
    Detalles: '',
    Servicios: []
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCita, setEditingCita] = useState(null);
  const [deletingCita, setDeletingCita] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Efecto para establecer el cliente cuando es un cliente logueado
  useEffect(() => {
    if (!isAdmin && clienteData) {
      setFormData(prev => ({ ...prev, Cliente: clienteData.id }));
    }
  }, [isAdmin, clienteData]);


  // Manejar selección de hora personalizada para abrir modal
  const handleSelectTimeForModal = (time) => {
    handleSelectTime(time);
    
    // Actualizar formData con la fecha y hora seleccionadas
    setFormData(prev => ({
      ...prev,
      Fecha: selectedDate.format('YYYY-MM-DD'),
      Hora: time.format('HH:mm')
    }));
    
    // Abrir modal para crear cita
    setIsModalOpen(true);
  };

  // Función para mostrar el selector de horas en móvil para el día seleccionado
  const handleShowTimeSelectorForSelected = () => {
    // Si hay una fecha seleccionada, usarla, de lo contrario usar hoy
    const dateToUse = selectedDate || moment();
    
    // Usar la nueva función que fuerza mostrar el selector
    handleForceShowTimeSelector(dateToUse);
  };

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Si cambia el cliente y estamos como admin, actualizar selectedCliente y limpiar Vehiculo
    if (name === 'Cliente' && isAdmin) {
      setSelectedCliente(value);
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        Vehiculo: '' // Limpiar la selección de vehículo cuando cambia el cliente
      }));
    } else {
      // Para otros campos, simplemente actualizar el valor
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Filtrar vehículos según el cliente seleccionado (memoizado para optimización)
  const filteredVehiculos = React.useMemo(() => {
    /*console.log('🚗 [DEBUG] Filtrando vehículos:', {
      isAdmin,
      selectedCliente,
      clienteData: clienteData ? { id: clienteData.id } : null,
      totalVehiculos: vehiculos.length,
      vehiculosStructure: vehiculos.length > 0 ? vehiculos[0] : 'No vehicles'
    });*/
    
    // Para administradores, filtrar por cliente seleccionado
    if (isAdmin) {
      if (!selectedCliente) {
        //console.log('🚗 [DEBUG] Admin sin cliente seleccionado - retornando array vacío');
        return [];
      }
      
      const filtered = vehiculos.filter(v => {
        const normalizedVehiculo = normalizeDataObject(v, 'vehiculo');
        const cliente = normalizedVehiculo.Cliente || normalizedVehiculo.cliente;
        
        /*console.log('🚗 [DEBUG] Verificando vehículo:', {
          vehiculoId: normalizedVehiculo.id,
          patente: normalizedVehiculo.Patente || normalizedVehiculo.patente,
          cliente: cliente,
          selectedCliente,
          match: Array.isArray(cliente) ? cliente[0] === selectedCliente : cliente === selectedCliente
        });*/
        
        if (!cliente) return false;
        
        if (Array.isArray(cliente)) {
          return cliente[0] === selectedCliente;
        } else {
          return cliente === selectedCliente;
        }
      });
      
      //console.log('🚗 [DEBUG] Vehículos filtrados para admin:', filtered.length, filtered);
      return filtered;
    }
    
    // Para clientes logueados, filtrar por su propio ID
    if (clienteData && clienteData.id) {
      const filtered = vehiculos.filter(v => {
        const normalizedVehiculo = normalizeDataObject(v, 'vehiculo');
        const cliente = normalizedVehiculo.Cliente || normalizedVehiculo.cliente;
        
        /*console.log('🚗 [DEBUG] Verificando vehículo para cliente:', {
          vehiculoId: normalizedVehiculo.id,
          patente: normalizedVehiculo.Patente || normalizedVehiculo.patente,
          cliente: cliente,
          clienteDataId: clienteData.id,
          match: Array.isArray(cliente) ? cliente[0] === clienteData.id : cliente === clienteData.id
        });*/
        
        if (!cliente) return false;
        
        if (Array.isArray(cliente)) {
          return cliente[0] === clienteData.id;
        } else {
          return cliente === clienteData.id;
        }
      });
      
      //console.log('🚗 [DEBUG] Vehículos filtrados para cliente:', filtered.length, filtered);
      return filtered;
    }
    
    //console.log('🚗 [DEBUG] Sin condiciones válidas - retornando array vacío');
    return [];
  }, [vehiculos, selectedCliente, isAdmin, clienteData]);
  
  // Efecto para manejar la selección de vehículos cuando cambia el cliente
  React.useEffect(() => {
    // Solo ejecutar si hay un cliente seleccionado
    if (!selectedCliente) {
      setFormData(prev => prev.Vehiculo ? { ...prev, Vehiculo: '' } : prev);
      return;
    }
    
    // Solo actualizar si hay exactamente un vehículo y es diferente al actual
    if (filteredVehiculos.length === 1 && formData.Vehiculo !== filteredVehiculos[0].id) {
      setFormData(prev => ({
        ...prev,
        Vehiculo: filteredVehiculos[0].id
      }));
    } else if (filteredVehiculos.length !== 1 && formData.Vehiculo) {
      // Limpiar solo si hay múltiples vehículos o ninguno y hay un vehículo seleccionado
      setFormData(prev => ({
        ...prev,
        Vehiculo: ''
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCliente, filteredVehiculos.length]);

  // Manejar envío del formulario (creación o edición)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSucursal) {
      toastService.warning("Selecciona una sucursal primero");
      return;
    }
    
    if (!formData.Vehiculo) {
      toastService.warning("Selecciona un vehículo");
      return;
    }
    
    if (selectedServicios.length === 0) {
      toastService.warning("Selecciona al menos un servicio");
      return;
    }
    
    try {
      setLoading(true);

      
      // Validar que se haya seleccionado al menos un servicio
      if (selectedServicios.length === 0) {
        toastService.warning("Selecciona al menos un servicio");
        setLoading(false);
        return;
      }
      

      
      // Formatear servicios seleccionados usando el servicio
      const serviciosFormateados = citasLogicService.formatearServiciosParaEnvio(selectedServicios);
      

      
      // Calcular tiempo total y hora fin usando el servicio
      const tiempoTotalCalculado = citasLogicService.calcularTiempoTotal(selectedServicios, servicios);
      
      // Obtener horario de atención para considerar en el cálculo
      const horarioAtencion = sucursalData?.HorarioAtencion ? 
        horarioService.parseHorarioAtencion(sucursalData.HorarioAtencion) : null;
      
      const resultado = citasLogicService.calcularHoraFin(
        formData.Hora, 
        selectedDate, 
        tiempoTotalCalculado, 
        horarioAtencion
      );
      
      // Preparar datos para enviar
      const formDataToSend = {
        ...formData,
        // Mapear la selección de vehículo (patente/id) al campo Patente esperado por toApi
        Patente: formData.Vehiculo,
        TiempoTotal: tiempoTotalCalculado,
        HoraFin: resultado.horaFin ? resultado.horaFin.format('HH:mm') : null,
        ServiciosSeleccionados: serviciosFormateados,
        Sucursal: selectedSucursal,
        ClienteID: isAdmin ? selectedCliente : (clienteData ? clienteData.id : null)
      };
      
      // Verificar que la sucursal sea válida
      if (!selectedSucursal) {
        console.error('ERROR: No hay sucursal seleccionada');
        toastService.error("Selecciona una sucursal para continuar");
        return;
      }
      

      
      let response;
      if (editingCita) {

        response = await citasApiService.updateCita(editingCita, formDataToSend);

        toastService.success("Cita actualizada correctamente");
      } else {

        response = await citasApiService.createCita(formDataToSend);

        toastService.success("Cita creada correctamente");
      }
      
      // Verificar si se guardaron los servicios
      if (response && response.ServiciosSeleccionados) {
  
      } else {
  
      }
      
      // Limpiar formulario y cerrar modal
      setFormData({
        Vehiculo: '',
        Motivo: '',
        Estado: 'Pendiente',
        Detalles: '',
        Servicios: []
      });
      setIsModalOpen(false);
      handleDeseleccionarTodos();
      
      // Recargar citas
      await refreshCitas();
      

    } catch (error) {
      console.error('ERROR al guardar cita:', error);
      console.error('Detalles del error:', error.message, error.stack);
      toastService.error(editingCita ? "Error al actualizar la cita" : "Error al crear la cita");

    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar una cita
  const handleDeleteCita = async () => {
    if (!deletingCita) return;
    
    try {
      await citasApiService.deleteCita(deletingCita.id);
      
      // Refrescar citas desde el servidor
      await refreshCitas();
      
      toastService.success('Cita eliminada correctamente');
      setIsDeleteModalOpen(false);
      setDeletingCita(null);
    } catch (error) {
      console.error('Error al eliminar la cita:', error);
      toastService.error('Error al eliminar la cita');
    }
  };
  
  // Función para abrir el modal de confirmación de eliminación
  const handleConfirmDeleteCita = (cita) => {
    // Simplemente establecer la cita a eliminar y abrir el modal de confirmación
    setDeletingCita(cita);
    setIsDeleteModalOpen(true);
  };
  
  // Función para editar una cita existente
  const handleEditCita = async (cita) => {
    // Verificar si la cita ya pasó (no se puede editar)
    const citaDateTime = moment(cita.Fecha + ' ' + cita.Hora, 'YYYY-MM-DD HH:mm');
    if (citaDateTime.isBefore(moment())) {
      toastService.warning("No se pueden modificar citas pasadas");
      return;
    }
    
    // Asegurarse de que los servicios estén cargados
    if (servicios.length === 0) {
      await fetchServicios();
    }
    
    // Establecer la fecha y hora seleccionadas usando el hook
    const citaDate = moment(cita.Fecha);
    handleSelectDate(citaDate);
    // La hora se establecerá cuando se abra el modal
    
    // Cargar los servicios seleccionados
    let serviciosSeleccionados = [];
    
    try {
      // Intentar obtener servicios seleccionados de la API
      if (cita.id) {
        const serviciosApi = await citasApiService.getServiciosSeleccionados(cita.id);
        if (serviciosApi && serviciosApi.length > 0) {
          serviciosSeleccionados = serviciosApi;
        } else if (cita.Servicios && cita.Servicios.length > 0) {
          // Si no hay servicios en la API, intentar usar los de la cita
          serviciosSeleccionados = cita.Servicios.map(servicioId => {
            const servicio = servicios.find(s => s.id === servicioId);
            return servicio || { id: servicioId };
          });
        } else if (cita.ServiciosSeleccionados && cita.ServiciosSeleccionados.length > 0) {
          // Usar servicios seleccionados de la cita si están disponibles
          serviciosSeleccionados = cita.ServiciosSeleccionados;
        } else {
          // Último recurso: buscar servicios por ID en la lista de servicios
          serviciosSeleccionados = servicios.filter(servicio => 
            cita.Servicios.includes(servicio.id)
          );
        }
      }
    } catch (error) {
      console.error('Error al cargar servicios seleccionados:', error);
    }
    
    // IMPORTANTE: Asegurarse de que los servicios seleccionados tengan el formato correcto
    // Esto es crucial para que la selección funcione correctamente en el formulario
    const serviciosFormateados = serviciosSeleccionados.map(servicio => {
      // Los servicios pueden venir con diferentes estructuras:
      // 1. Como objetos con ServicioID (desde la API de servicios seleccionados)
      // 2. Como objetos con id directo
      // 3. Como objetos con campo Servicio
      
      let servicioId = null;
      
      // Determinar el ID del servicio según la estructura
      if (servicio.ServicioID) {
        // Estructura desde API: {id: 20, ServicioID: 8, ServicioNombre: '...'}
        servicioId = servicio.ServicioID;
      } else if (servicio.id) {
        // Estructura directa: {id: 8, Nombre: '...'}
        servicioId = servicio.id;
      } else if (servicio.Servicio) {
        // Estructura con campo Servicio: {Servicio: 8}
        servicioId = Array.isArray(servicio.Servicio) ? servicio.Servicio[0] : servicio.Servicio;
      }
      
      // Buscar el servicio completo en la lista de servicios disponibles
      const servicioCompleto = servicios.find(s => s.id === servicioId);
      
      // Si encontramos el servicio completo, usarlo como base
      if (servicioCompleto) {
        return {
          ...servicioCompleto,
          // Asegurarse de que el ID esté correctamente asignado
          id: servicioCompleto.id,
          // Mantener la referencia al ID del servicio en el campo Servicio
          Servicio: servicioCompleto.id
        };
      }
      
      // Si no lo encontramos, crear un objeto básico con el ID que tenemos
      return {
        id: servicioId,
        Servicio: servicioId,
        Nombre: servicio.ServicioNombre || servicio.Nombre || `Servicio ${servicioId}`,
        Precio: servicio.PrecioEstimado || servicio.Precio || 0
      };
    });
    
    // Debug: mostrar qué servicios se están cargando
    //console.log('🔍 [CalendarioCitas] Servicios seleccionados originales:', serviciosSeleccionados);
    //console.log('🔍 [CalendarioCitas] Servicios formateados para selección:', serviciosFormateados);
    //console.log('🔍 [CalendarioCitas] Lista completa de servicios disponibles:', servicios);
    
    // Primero limpiar servicios seleccionados
    handleDeseleccionarTodos();
    
    // Luego agregar cada servicio usando la función del hook
    serviciosFormateados.forEach(servicio => {
      if (servicio.id) {
        //console.log('🔍 [CalendarioCitas] Seleccionando servicio ID:', servicio.id);
        handleServicioSelect(servicio.id);
      }
    });
    
    // Si es cliente y la cita no le pertenece, no permitir edición
    if (!isAdmin && clienteData && cita.clienteData && cita.clienteData.id !== clienteData.id) {
      toastService.warning("No puedes editar citas de otros clientes");
      return;
    }
    
    // Obtener el ID del vehículo de la cita
    let patenteId = '';
    
    // Comprobar todas las posibles ubicaciones del ID del vehículo
    if (cita.patenteId) {
      // Si la cita ya tiene un patenteId explícito (desde fetchCitas)
      patenteId = cita.patenteId;
      // //console.log('Usando patenteId explícito de la cita:', patenteId);
    } else if (cita.vehiculoData && cita.vehiculoData.id) {
      // Si la cita tiene datos del vehículo
      patenteId = cita.vehiculoData.id;
      // //console.log('Usando ID del vehiculoData:', patenteId);
    } else if (Array.isArray(cita.Vehiculo) && cita.Vehiculo.length > 0) {
      // Si Vehiculo es un array
      patenteId = cita.Vehiculo[0];
      // //console.log('Usando primer elemento del array Vehiculo:', patenteId);
    } else if (typeof cita.Vehiculo === 'string' && cita.Vehiculo) {
      // Si Vehiculo es un string
      patenteId = cita.Vehiculo;
      // //console.log('Usando Vehiculo como string:', patenteId);
    }
    
    // //console.log('ID del vehículo seleccionado para la cita:', patenteId);
    
    // Buscar el vehículo completo para obtener el cliente asociado
    const vehiculoCompleto = vehiculos.find(v => v.id === patenteId);
    // //console.log('Vehículo completo encontrado:', vehiculoCompleto ? vehiculoCompleto.id : 'No encontrado');
    
    // Obtener el cliente del vehículo o de la cita
    let clienteId = '';
    
    // Comprobar todas las posibles ubicaciones del ID del cliente
    if (cita.clienteId) {
      // Si la cita ya tiene un clienteId explícito (desde fetchCitas)
      clienteId = cita.clienteId;
      // //console.log('Usando clienteId explícito de la cita:', clienteId);
    } else if (cita.clienteData && cita.clienteData.id) {
      // Si la cita tiene datos del cliente
      clienteId = cita.clienteData.id;
      // //console.log('Usando ID del clienteData:', clienteId);
    } else if (vehiculoCompleto && vehiculoCompleto.Cliente) {
      // Si el vehículo tiene cliente asociado
      if (Array.isArray(vehiculoCompleto.Cliente) && vehiculoCompleto.Cliente.length > 0) {
        clienteId = vehiculoCompleto.Cliente[0];
      } else if (typeof vehiculoCompleto.Cliente === 'string') {
        clienteId = vehiculoCompleto.Cliente;
      }
      // //console.log('Usando cliente del vehículo:', clienteId);
    } else if (Array.isArray(cita.Cliente) && cita.Cliente.length > 0) {
      // Si Cliente es un array
      clienteId = cita.Cliente[0];
      // //console.log('Usando primer elemento del array Cliente:', clienteId);
    } else if (typeof cita.Cliente === 'string' && cita.Cliente) {
      // Si Cliente es un string
      clienteId = cita.Cliente;
      // //console.log('Usando Cliente como string:', clienteId);
    }
    
    // //console.log('ID del cliente seleccionado para la cita:', clienteId);
    
    // Establecer el cliente seleccionado
    if (clienteId) {
      // //console.log('Seleccionando cliente:', clienteId);
      setSelectedCliente(clienteId);
    }
    
    // Cargar los datos en el formulario
    const nuevoFormData = {
      id: cita.id, // Importante para saber que estamos editando
      Vehiculo: patenteId,
      Cliente: clienteId, // Usar el cliente obtenido
      Motivo: cita.Motivo || '',
      Estado: cita.Estado || 'Pendiente',
      Detalles: cita.Detalles || '',
      Fecha: cita.Fecha,
      Hora: cita.Hora
    };
    
    // //console.log('Cargando datos en el formulario:', nuevoFormData);
    setFormData(nuevoFormData);
    
    // El tiempo total se calcula automáticamente en el hook cuando se seleccionan servicios
    
    // Marcar que estamos editando esta cita
    setEditingCita(cita.id);
    
    // Abrir el modal
    setIsModalOpen(true);
    
    // Ya no necesitamos un setTimeout aquí, ya que los datos ya están en el formData
    // y el useEffect en AppointmentForm ya no intenta actualizar el estado
    // //console.log('Datos listos para el formulario - Patente:', patenteId, 'Cliente:', clienteId);
  };
  
  // Manejar creación de cliente y vehículo desde el modal
  const handleClienteVehiculoCreado = async (cliente, vehiculo) => {
    try {
      // Aquí irían las llamadas reales a las APIs para crear cliente y vehículo
      // Por ahora simulamos con toasts
      toastService.success(`Cliente ${cliente.nombre} y vehículo ${vehiculo.marca} ${vehiculo.modelo} creados exitosamente`);
      
      // Actualizar las listas locales (en producción esto se haría con refresh de APIs)
      // Para demo, simplemente mostramos un toast y refrescamos datos
      await refreshCitas();
      
      // Seleccionar automáticamente el cliente creado
      setSelectedCliente(cliente.id);
      
    } catch (error) {
      console.error('Error creando cliente y vehículo:', error);
      toastService.error('Error al crear cliente y vehículo');
    }
  };

  // Cancelar creación o edición de cita
  const handleCancel = () => {
    setIsModalOpen(false);
    handleCloseTimeSelector();
    handleDeseleccionarTodos();
    setEditingCita(null);
    setFormData({
      Vehiculo: '',
      Motivo: '',
      Estado: 'Pendiente',
      Detalles: '',
      Servicios: []
    });
  };

  return (
    <div className="bg-gray-900 text-white p-4 rounded-lg shadow-lg">
      {loading && !isModalOpen && (
        <div className="absolute inset-0 flex justify-center items-center bg-gray-900 bg-opacity-50 z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
      
      <div className="flex flex-col md:flex-row md:space-x-6">
        {/* Calendario - siempre visible tanto en escritorio como en móvil */}
        <div className="block md:w-1/2">
          <CalendarHeader 
            currentDate={currentDate}
            onPrevMonth={() => handleMonthChange(-1)}
            onNextMonth={() => handleMonthChange(1)}
            onToday={handleGoToToday}
          />
          
          <CalendarGrid 
            currentDate={currentDate}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
            hasCitasOnDay={hasCitasOnDay}
            isAdmin={isAdmin}
            isSuperAdmin={isSuperAdmin}
            onGoToToday={handleGoToToday}
            onShowTimeSelector={handleShowTimeSelectorForSelected}
          />
        </div>
        
        {/* Selector de hora - visible siempre en escritorio, en móvil solo cuando se selecciona una fecha */}
        <div className={`hidden md:block md:w-1/2`}>
          {showTimeSelector && (
            <TimeSelector 
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onSelectTime={handleSelectTimeForModal}
              isTimeSlotAvailable={isTimeSlotAvailable}
              getCapacidadInfo={getCapacidadInfo}
              timeSlots={generateTimeSlots()}
            />
          )}
        </div>
      </div>
      
      {/* Modal de selector de hora para móvil - ahora se superpone al calendario */}
      {isMobile && showTimeSelector && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-80"
          onClick={handleCloseTimeSelector} // Cerrar al hacer clic en el fondo
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }} // Asegurar posicionamiento fijo
        >
          <div 
            className="bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-600"
            onClick={(e) => e.stopPropagation()} // Evitar que los clics dentro del modal lo cierren
            style={{ position: 'relative', zIndex: 51 }} // Asegurar que está por encima
          >
            <div className="p-3 border-b border-gray-700 flex justify-between items-center">
              <h2 className="text-lg font-medium">Selecciona una hora</h2>
              <button 
                onClick={handleCloseTimeSelector}
                className="p-1 rounded-full hover:bg-gray-700 transition-colors"
                aria-label="Cerrar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <TimeSelector 
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onSelectTime={handleSelectTimeForModal}
              isTimeSlotAvailable={isTimeSlotAvailable}
              getCapacidadInfo={getCapacidadInfo}
              timeSlots={generateTimeSlots()}
            />
          </div>
        </div>
      )}
      
      {/* Lista de citas del día seleccionado */}
      {selectedDate && (
        <AppointmentList 
          selectedDate={selectedDate}
          citas={getCitasOnDay(selectedDate)}
          vehiculos={vehiculos}
          clientes={clientes}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          clienteData={clienteData}
          onEditCita={handleEditCita}
          onDeleteCita={handleConfirmDeleteCita}
          onRefreshCitas={refreshCitas}
        />
      )}
      
      {/* Modal de creación de cita */}
      <AppointmentModal 
        isOpen={isModalOpen}
        selectedDate={selectedDate}
        editingCita={editingCita}
        formData={formData}
        selectedCliente={selectedCliente}
        clienteData={clienteData}
        isAdmin={isAdmin}
        clientes={clientes}
        filteredVehiculos={filteredVehiculos}
        servicios={servicios}
        selectedServicios={selectedServicios}
        selectedTime={selectedTime}
        horaFin={horaFin}
        horaFinAjustada={horaFinAjustada}
        tiempoTotal={tiempoTotal}
        loading={loading}
        handleChange={handleChange}
        handleServicioSelect={handleServicioSelect}
        handleDeseleccionarTodos={handleDeseleccionarTodos}
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
        onClienteVehiculoCreado={handleClienteVehiculoCreado}
      />
      
      {/* Modal de confirmación para eliminar cita */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeletingCita(null);
        }}
        title="Confirmar eliminación"
      >
        <div className="p-4">
          <p className="mb-4">¿Estás seguro de que deseas eliminar esta cita?</p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setIsDeleteModalOpen(false);
                setDeletingCita(null);
              }}
              className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              onClick={handleDeleteCita}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              disabled={loading}
            >
              {loading ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CalendarioCitas;